import {
  address,
  Address,
  GetAccountInfoApi,
  getAddressEncoder,
  none,
  ReadonlyUint8Array,
  Rpc,
  some,
  wrapNullable,
} from '@solana/kit';
import { MetadataArgs, TokenProgramVersion, TokenStandard } from './generated';
import {
  DasApiAsset,
  GetAssetApi,
  GetAssetProofApi,
  GetAssetProofRpcResponse,
} from './rpc';
import { fetchMerkleTree } from '@mcintyre94/spl-account-compression';
import { isValidLeafSchemaV2Flags, LeafSchemaV2Flags } from './flags';

export type AssetWithProof = {
  leafOwner: Address;
  leafDelegate: Address;
  merkleTree: Address;
  root: ReadonlyUint8Array;
  dataHash: ReadonlyUint8Array;
  creatorHash: ReadonlyUint8Array;
  collection_hash?: ReadonlyUint8Array;
  asset_data_hash?: ReadonlyUint8Array;
  flags?: number;
  nonce: number;
  index: number;
  proof: Address[];
  metadata: MetadataArgs;
  rpcAsset: DasApiAsset;
  rpcAssetProof: GetAssetProofRpcResponse;
};

type GetAssetWithProofOptions = {
  /* Define the options properties here */
  truncateCanopy?: boolean;
};

export async function getAssetWithProof(
  rpc: Rpc<GetAccountInfoApi & GetAssetApi & GetAssetProofApi>,
  assetId: Address,
  options?: GetAssetWithProofOptions
): Promise<AssetWithProof> {
  const [rpcAsset, rpcAssetProof] = await Promise.all([
    rpc
      .getAsset({
        id: assetId,
        displayOptions: { showUnverifiedCollections: true },
      })
      .send(),
    rpc.getAssetProof({ id: assetId }).send(),
  ]);

  let { proof } = rpcAssetProof;
  if (options?.truncateCanopy) {
    const merkleTreeAccount = await fetchMerkleTree(rpc, rpcAssetProof.tree_id);
    const canopyDepth = Math.log2(merkleTreeAccount.data.canopy.length + 2) - 1;
    proof = rpcAssetProof.proof.slice(
      0,
      canopyDepth === 0 ? undefined : -canopyDepth
    );
  }

  const collectionString = (rpcAsset.grouping ?? []).find(
    (group) => group.group_key === 'collection'
  )?.group_value;

  const metadata: MetadataArgs = {
    name: rpcAsset.content?.metadata?.name ?? '',
    symbol: rpcAsset.content?.metadata?.symbol ?? '',
    uri: rpcAsset.content?.json_uri,
    sellerFeeBasisPoints: rpcAsset.royalty?.basis_points,
    primarySaleHappened: rpcAsset.royalty?.primary_sale_happened,
    isMutable: rpcAsset.mutable,
    editionNonce: wrapNullable(rpcAsset.supply?.edition_nonce),
    tokenStandard: some(TokenStandard.NonFungible),
    collection: collectionString
      ? some({ key: address(collectionString), verified: true })
      : none(),
    uses: none(),
    tokenProgramVersion: TokenProgramVersion.Original,
    creators: rpcAsset.creators,
  };

  const addressEncoder = getAddressEncoder();

  const collectionHashBytes = rpcAsset.compression.collection_hash
    ? addressEncoder.encode(rpcAsset.compression.collection_hash)
    : undefined;
  const assetDataHashBytes = rpcAsset.compression.asset_data_hash
    ? addressEncoder.encode(rpcAsset.compression.asset_data_hash)
    : undefined;

  const rawFlags = rpcAsset.compression.flags;
  const flagsValue = isValidLeafSchemaV2Flags(rawFlags)
    ? (rawFlags as LeafSchemaV2Flags)
    : undefined;

  return {
    leafOwner: rpcAsset.ownership.owner,
    leafDelegate: rpcAsset.ownership.delegate
      ? rpcAsset.ownership.delegate
      : rpcAsset.ownership.owner,
    merkleTree: rpcAssetProof.tree_id,
    root: addressEncoder.encode(rpcAssetProof.root),
    dataHash: addressEncoder.encode(rpcAsset.compression.data_hash),
    creatorHash: addressEncoder.encode(rpcAsset.compression.creator_hash),
    collection_hash: collectionHashBytes,
    asset_data_hash: assetDataHashBytes,
    flags: flagsValue,
    nonce: rpcAsset.compression.leaf_id,
    index: rpcAssetProof.node_index - 2 ** rpcAssetProof.proof.length,
    proof,
    metadata,
    rpcAsset,
    rpcAssetProof,
  };
}
